import type { ReactNode } from "react";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";
import { useTranslation } from "../../i18n";
import { Button } from "./button";
import { Pagination, PaginationContent, PaginationItem } from "./pagination";
import { Spinner } from "./spinner";

/**
 * Decide the next step when the user pages past the loaded frontier: keep
 * fetching until the target page is full or both upstream cursors run dry, so
 * only a true last page renders partially.
 */
export function frontierStep(loaded: number, target: number, pageSize: number, hasMore: boolean, rounds: number): "advance" | "fetch" | "stop" {
  const start = target * pageSize;
  if (loaded > start) return loaded >= start + pageSize || !hasMore || rounds >= 3 ? "advance" : "fetch";
  return !hasMore || rounds >= 3 ? "stop" : "fetch";
}

export interface ListPaginationProps {
  pageIndex?: number;
  hasPrevious: boolean;
  hasNext: boolean;
  loading?: boolean;
  summary?: ReactNode;
  label?: string;
  onPrevious(): void;
  onNext(): void;
}

export function ListPagination({ pageIndex, hasPrevious, hasNext, loading = false, summary, label, onPrevious, onNext }: Readonly<ListPaginationProps>) {
  const { t } = useTranslation();
  return <Pagination aria-label={label ?? t("common.pagination")} aria-busy={loading} className="mt-4 flex-wrap border-t border-border pt-4">
    <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm tabular-nums" aria-live="polite">
      {pageIndex !== undefined ? <span className="font-medium">{t("common.pageNumber", { page: pageIndex + 1 })}</span> : null}
      {summary ? <span className="text-xs text-muted-foreground">{summary}</span> : null}
    </div>
    <PaginationContent className="ml-auto">
      <PaginationItem><Button aria-label={t("workspace.previous")} variant="outline" size="sm" className="min-w-24" disabled={loading || !hasPrevious} onClick={onPrevious}>
        <ChevronLeftIcon aria-hidden="true" />{t("workspace.previous")}
      </Button></PaginationItem>
      <PaginationItem><Button aria-label={t("workspace.next")} variant="outline" size="sm" className="min-w-24" disabled={loading || !hasNext} onClick={onNext}>
        {t("workspace.next")}{loading ? <Spinner aria-hidden="true" role={undefined} aria-label={undefined} /> : <ChevronRightIcon aria-hidden="true" />}
      </Button></PaginationItem>
    </PaginationContent>
    {loading ? <span className="sr-only" role="status">{t("common.loading")}</span> : null}
  </Pagination>;
}
